import { inject, observer } from "mobx-react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap"
import BlockKitStore from "../stores/BlockKitStore";
import { useEffect, useState } from "react";
import { PermissionEntityType, UserPermissionModel } from "../controllers/StudioAdminController";
import React from "react";
import { AdminUserModel } from "../models/AccountPermissionModel";
import { t } from "i18next";


interface IProps<T extends { id?: string }> {
  blockKitStore?: BlockKitStore;
  entityType: PermissionEntityType;
  entity?: T;
  isShow: boolean;
  onClose: () => void;
}

const PermissionModalInner = <T extends { id?: string }>({
  blockKitStore,
  entityType,
  entity,
  isShow,
  onClose
}: IProps<T>) => {
  const [permissions, setPermissions] = useState<UserPermissionModel[]>([]);
  const [users, setUsers] = useState<AdminUserModel[]>([]);
  const [changedUsers, setChangedUsers] = useState<string[]>([]);
  const [isCascadePermissionToBlocks, setIsCascadePermissionToBlocks] = useState<boolean>(false);

  useEffect(() => {
    if (isShow && entity?.id) {
      blockKitStore?.studioController.getEntityPermissionsAsync(entityType, entity.id).then(setPermissions);
      blockKitStore?.studioController.getUsersAsync().then(setUsers);
    }
  }, [isShow]);

  const handlePermissionChange = (userId: string, type: string, checked: boolean) => {
    setPermissions(prev => {
      const existing = prev.find(p => p.userId === userId);

      if (existing) {
        // 이미 권한 데이터가 있는 유저 → 업데이트
        return prev.map(p =>
          p.userId === userId
            ? {
              ...p,
              permissions: checked
                ? [...new Set([...p.permissions, type])]
                : p.permissions.filter(per => per !== type),
            }
            : p
        );
      } else {
        // 권한 데이터가 없던 유저 → 새로 추가
        return [
          ...prev,
          {
            userId,
            categoryId: entity?.id!,
            permissions: checked ? [type] : [],
          },
        ];
      }
    });

    // 변경된 유저 기록
    setChangedUsers(prev =>
      prev.includes(userId) ? prev : [...prev, userId]
    );
  };

  const handleSave = async () => {
    if (!entity?.id) {
      alert("카테고리를 선택해주세요");
      return;
    }

    const updatedUsers = permissions.filter(p =>
      changedUsers.includes(p.userId)
    );

    // 예시 API 호출
    blockKitStore?.studioController.postEntityPermissionAsync(entityType, entity?.id, updatedUsers, isCascadePermissionToBlocks);
    alert("권한이 갱신되었습니다!");
    setChangedUsers([]); // 초기화
    onClose();
  };

  return (
    <Modal size="lg" show={isShow} onHide={() => onClose()}>
      <Modal.Header closeButton>권한 설정</Modal.Header>
      <Modal.Body style={{ maxHeight: "500px", overflowY: "auto" }}>
        <Form.Group
          hidden={entityType !== "categories"}
        >
          <Form.Check
            type="switch"
            label="디자인에도 같은 권한 적용하기"
            checked={isCascadePermissionToBlocks}
            onChange={(e) => setIsCascadePermissionToBlocks(e.target.checked)}
          />
        </Form.Group>
        <Table>
          <thead>
            <tr>
              <th>유저</th>
              <th>이름</th>
              <th>역할</th>
              <th>읽기</th>
              <th>쓰기</th>
            </tr>
          </thead>
          <tbody>
            {users?.filter(i => i.role === "publisher")?.map(i => {
              return (
                <tr>
                  <td>
                    <Form.Label>{i.email}</Form.Label>
                  </td>
                  <td>
                    <Form.Label>{i.name}</Form.Label>
                  </td>
                  <td>
                    <Form.Label>{i.role}</Form.Label>
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={permissions?.some(
                        p => p.userId === i.userId && p.permissions.includes("read")
                      )}
                      onChange={e =>
                        handlePermissionChange(i.userId, "read", e.target.checked)
                      }
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={permissions?.some(
                        p => p.userId === i.userId && p.permissions.includes("write")
                      )}
                      onChange={e =>
                        handlePermissionChange(i.userId, "write", e.target.checked)
                      }
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSave}>
          {t("button-save")}
        </Button>
        <Button variant="secondary" onClick={() => onClose()}>
          {t("button-close")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
};

const PermissionModal = inject("blockKitStore")(observer(PermissionModalInner));

export default PermissionModal;
